import React, { useState, useEffect } from 'react';
import axios from '../services/api';
import { Disc, ArrowRight, RefreshCw } from 'lucide-react';
import { Card } from '../components/ds/Card';
import { Button } from '../components/ds/Button';

export default function InventoryPage() {
  const [heads, setHeads] = useState([]);
  const [shafts, setShafts] = useState([]);
  const [balls, setBalls] = useState([]);
  const [headsTotal, setHeadsTotal] = useState(0);
  const [shaftsTotal, setShaftsTotal] = useState(0);
  const [ballsTotal, setBallsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [headFilter, setHeadFilter] = useState('');
  const [shaftFilter, setShaftFilter] = useState('');
  const [ballFilter, setBallFilter] = useState('');
  const [headCategory, setHeadCategory] = useState('');
  const [shaftCategory, setShaftCategory] = useState('');
  const [selectedHead, setSelectedHead] = useState(null);
  const [selectedShaft, setSelectedShaft] = useState(null);
  const [selectedBall, setSelectedBall] = useState(null);

  const loadInventory = async () => {
    setLoading(true);
    try {
      const [headsRes, shaftsRes, ballsRes] = await Promise.all([
        axios.get(`/inventory/heads${headCategory ? `?category=${headCategory}` : ''}`),
        axios.get(`/inventory/shafts${shaftCategory ? `?category=${shaftCategory}` : ''}`),
        axios.get('/inventory/balls')
      ]);
      setHeads(headsRes.data?.items || []);
      setHeadsTotal(headsRes.data?.total || 0);
      setShafts(shaftsRes.data?.items || []);
      setShaftsTotal(shaftsRes.data?.total || 0);
      setBalls(ballsRes.data?.balls || []);
      setBallsTotal(ballsRes.data?.balls?.length || 0);
    } catch (e) { console.error('Failed to load inventory', e); }
    setLoading(false);
  };

  useEffect(() => { loadInventory(); }, [headCategory, shaftCategory]);

  const filteredHeads = heads.filter(h =>
    !headFilter || (h.manufacturer || '').toLowerCase().includes(headFilter.toLowerCase()) ||
    (h.model_name || '').toLowerCase().includes(headFilter.toLowerCase())
  );

  const filteredShafts = shafts.filter(s =>
    !shaftFilter || (s.manufacturer || '').toLowerCase().includes(shaftFilter.toLowerCase()) ||
    (s.model_name || '').toLowerCase().includes(shaftFilter.toLowerCase())
  );

  const filteredBalls = balls.filter(b =>
    !ballFilter || (b.brand || '').toLowerCase().includes(ballFilter.toLowerCase()) ||
    (b.model || '').toLowerCase().includes(ballFilter.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-heading font-bold text-text-primary">Inventory Database</h2>
        <Button onClick={loadInventory} disabled={loading} variant="secondary" size="sm" icon={RefreshCw}>
          Refresh
        </Button>
      </div>

      {/* Heads Section */}
      <Card className="overflow-hidden p-0">
        <div className="bg-background border-b border-border p-5 flex flex-wrap gap-3 items-center justify-between">
          <h3 className="font-heading font-bold text-text-primary flex items-center gap-2"><Disc size={18} className="text-primary" /> Club Heads <span className="text-text-muted font-normal text-sm">({filteredHeads.length} of {headsTotal})</span></h3>
          <div className="flex gap-2">
            <select value={headCategory} onChange={e => setHeadCategory(e.target.value)} className="input-dark text-sm">
              <option value="">All Categories</option>
              <option value="Driver">Driver</option>
              <option value="Fairway">Fairway</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Iron">Iron</option>
              <option value="Wedge">Wedge</option>
            </select>
            <input placeholder="Search..." value={headFilter} onChange={e => setHeadFilter(e.target.value)} className="input-dark text-sm w-40" />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-background text-text-muted sticky top-0">
              <tr><th className="p-2 text-left">Manufacturer</th><th className="p-2 text-left">Model</th><th className="p-2 text-center">Category</th><th className="p-2 text-center">Profile</th><th className="p-2 text-center">Dexterity</th><th className="p-2 text-right">Loft</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-8 text-center text-text-muted"><RefreshCw className="animate-spin mx-auto" size={16}/></td></tr>}
              {!loading && filteredHeads.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-text-muted">No heads found</td></tr>}
              {!loading && filteredHeads.map(h => (
                <React.Fragment key={h.id}>
                  <tr onClick={() => setSelectedHead(selectedHead?.id === h.id ? null : h)} className={`border-t border-border hover:bg-surface-light cursor-pointer ${selectedHead?.id === h.id ? 'bg-blue-900/30' : ''}`}>
                    <td className="p-2 text-text-secondary">{h.manufacturer || 'null'}</td>
                    <td className="p-2 text-text-primary font-medium">{h.model_name || 'null'}</td>
                    <td className="p-2 text-center"><span className={`text-xs px-2 py-0.5 rounded ${h.category === 'Driver' ? 'bg-blue-900/50 text-blue-300' : h.category === 'Iron' ? 'bg-emerald-900/50 text-emerald-300' : h.category === 'Wedge' ? 'bg-orange-900/50 text-orange-300' : 'bg-surface text-text-secondary'}`}>{h.category || 'null'}</span></td>
                    <td className="p-2 text-center text-text-secondary text-xs">{h.head_profile || 'null'}</td>
                    <td className="p-2 text-center"><span className="text-xs px-2 py-0.5 rounded bg-surface text-text-secondary">{h.dexterity || 'null'}</span></td>
                    <td className="p-2 text-right text-text-secondary">{h.loft ? `${h.loft}°` : 'null'}</td>
                  </tr>
                  {selectedHead?.id === h.id && (
                    <tr className="bg-blue-950/30 border-t border-blue-800">
                      <td colSpan={5} className="p-4">
                        <div className="text-sm">
                          <div className="font-bold text-text-primary mb-2">{h.manufacturer || 'null'} {h.model_name || 'null'}</div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div><span className="text-text-muted">Category:</span> <span className="text-text-primary">{h.category || 'null'}</span></div>
                            <div><span className="text-text-muted">Profile:</span> <span className="text-text-primary">{h.head_profile || 'null'}</span></div>
                            <div><span className="text-text-muted">Loft:</span> <span className="text-text-primary">{h.loft ? `${h.loft}°` : 'null'}</span></div>
                            <div><span className="text-text-muted">Lie Angle:</span> <span className="text-text-primary">{h.lie_angle ? `${h.lie_angle}°` : 'null'}</span></div>
                            <div><span className="text-text-muted">MOI:</span> <span className="text-text-primary">{h.moi_profile || 'null'}</span></div>
                            <div><span className="text-text-muted">Spin Bias:</span> <span className="text-text-primary">{h.spin_bias || 'null'}</span></div>
                            <div><span className="text-text-muted">Dexterity:</span> <span className="text-text-primary">{h.dexterity || 'null'}</span></div>
                            <div><span className="text-text-muted">Year:</span> <span className="text-text-primary">{h.model_year || 'null'}</span></div>
                          </div>
                          {(h.msrp || h.street_price) && (
                            <div className="mt-3 pt-3 border-t border-blue-800 flex gap-6 text-xs">
                              {h.msrp && <div><span className="text-text-muted">MSRP:</span> <span className="text-text-primary">${h.msrp}</span></div>}
                              {h.street_price && <div><span className="text-text-muted">Street:</span> <span className="text-emerald-400">${h.street_price}</span></div>}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Shafts Section */}
      <Card padding="p-0" className="overflow-hidden">
        <div className="bg-background p-4 flex flex-wrap gap-3 items-center justify-between">
          <h3 className="font-bold text-text-primary flex items-center gap-2"><ArrowRight size={16} className="text-emerald-500" /> Shafts <span className="text-text-muted font-normal">({filteredShafts.length} of {shaftsTotal})</span></h3>
          <div className="flex gap-2">
            <select value={shaftCategory} onChange={e => setShaftCategory(e.target.value)} className="bg-zinc-900 border border-zinc-700 px-2 py-1 rounded text-sm text-text-primary">
              <option value="">All Categories</option>
              <option value="Driver">Driver/Wood</option>
              <option value="Iron">Iron</option>
            </select>
            <input placeholder="Search..." value={shaftFilter} onChange={e => setShaftFilter(e.target.value)} className="bg-zinc-900 border border-zinc-700 px-2 py-1 rounded text-sm text-text-primary w-40" />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-background text-text-muted sticky top-0">
              <tr><th className="p-2 text-left">Manufacturer</th><th className="p-2 text-left">Model</th><th className="p-2 text-center">Flex</th><th className="p-2 text-center">Weight</th><th className="p-2 text-center">Material</th><th className="p-2 text-center">Launch</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-8 text-center text-text-muted"><RefreshCw className="animate-spin mx-auto" size={16}/></td></tr>}
              {!loading && filteredShafts.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-text-muted">No shafts found</td></tr>}
              {!loading && filteredShafts.map(s => (
                <React.Fragment key={s.id}>
                  <tr onClick={() => setSelectedShaft(selectedShaft?.id === s.id ? null : s)} className={`border-t border-border hover:bg-surface-light cursor-pointer ${selectedShaft?.id === s.id ? 'bg-emerald-900/30' : ''}`}>
                    <td className="p-2 text-text-secondary">{s.manufacturer || 'null'}</td>
                    <td className="p-2 text-text-primary font-medium">{s.model_name || 'null'}</td>
                    <td className="p-2 text-center"><span className="bg-surface text-text-secondary text-xs px-2 py-0.5 rounded font-mono">{s.flex || 'null'}</span></td>
                    <td className="p-2 text-center text-text-secondary">{s.weight_g ? `${s.weight_g}g` : 'null'}</td>
                    <td className="p-2 text-center"><span className={`text-xs px-2 py-0.5 rounded ${s.material === 'Steel' ? 'bg-zinc-700 text-text-secondary' : 'bg-purple-900/50 text-purple-300'}`}>{s.material || 'Graphite'}</span></td>
                    <td className="p-2 text-center"><span className={`text-xs px-2 py-0.5 rounded ${s.launch_profile === 'Low' ? 'bg-blue-900/50 text-blue-300' : s.launch_profile === 'High' ? 'bg-orange-900/50 text-orange-300' : 'bg-surface text-text-secondary'}`}>{s.launch_profile || 'null'}</span></td>
                  </tr>
                  {selectedShaft?.id === s.id && (
                    <tr className="bg-emerald-950/30 border-t border-emerald-800">
                      <td colSpan={6} className="p-4">
                        <div className="text-sm">
                          <div className="font-bold text-text-primary mb-2">{s.manufacturer || 'null'} {s.model_name || 'null'}</div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div><span className="text-text-muted">Flex:</span> <span className="text-text-primary">{s.flex || 'null'}</span></div>
                            <div><span className="text-text-muted">Weight:</span> <span className="text-text-primary">{s.weight_g ? `${s.weight_g}g` : 'null'}</span></div>
                            <div><span className="text-text-muted">Material:</span> <span className="text-text-primary">{s.material || 'Graphite'}</span></div>
                            <div><span className="text-text-muted">Launch:</span> <span className="text-text-primary">{s.launch_profile || 'Mid'}</span></div>
                            <div><span className="text-text-muted">Torque:</span> <span className="text-text-primary">{s.torque ? `${s.torque}°` : 'null'}</span></div>
                            <div><span className="text-text-muted">Kick Point:</span> <span className="text-text-primary">{s.kick_point || 'null'}</span></div>
                            <div><span className="text-text-muted">Length:</span> <span className="text-text-primary">{s.length_inch ? `${s.length_inch}"` : 'null'}</span></div>
                            <div><span className="text-text-muted">Category:</span> <span className="text-text-primary">{s.category || 'null'}</span></div>
                          </div>
                          {(s.msrp || s.street_price) && (
                            <div className="mt-3 pt-3 border-t border-emerald-800 flex gap-6 text-xs">
                              {s.msrp && <div><span className="text-text-muted">MSRP:</span> <span className="text-text-primary">${s.msrp}</span></div>}
                              {s.street_price && <div><span className="text-text-muted">Street:</span> <span className="text-emerald-400">${s.street_price}</span></div>}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Golf Balls Section */}
      <Card padding="p-0" className="overflow-hidden">
        <div className="bg-background p-4 flex flex-wrap gap-3 items-center justify-between">
          <h3 className="font-bold text-text-primary flex items-center gap-2"><Disc size={16} className="text-yellow-500" /> Golf Balls <span className="text-text-muted font-normal">({filteredBalls.length} of {ballsTotal})</span></h3>
          <div className="flex gap-2">
            <input placeholder="Search..." value={ballFilter} onChange={e => setBallFilter(e.target.value)} className="bg-zinc-900 border border-zinc-700 px-2 py-1 rounded text-sm text-text-primary w-40" />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-background text-text-muted sticky top-0">
              <tr><th className="p-2 text-left">Brand</th><th className="p-2 text-left">Model</th><th className="p-2 text-center">Spin Profile</th><th className="p-2 text-center">Feel</th><th className="p-2 text-center">Compression</th><th className="p-2 text-right">Price</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-8 text-center text-text-muted"><RefreshCw className="animate-spin mx-auto" size={16}/></td></tr>}
              {!loading && filteredBalls.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-text-muted">No golf balls found</td></tr>}
              {!loading && filteredBalls.map(b => (
                <React.Fragment key={b.id}>
                  <tr onClick={() => setSelectedBall(selectedBall?.id === b.id ? null : b)} className={`border-t border-border hover:bg-surface-light cursor-pointer ${selectedBall?.id === b.id ? 'bg-yellow-900/30' : ''}`}>
                    <td className="p-2 text-text-secondary">{b.brand || 'null'}</td>
                    <td className="p-2 text-text-primary font-medium">{b.model || 'null'}</td>
                    <td className="p-2 text-center"><span className={`text-xs px-2 py-0.5 rounded ${b.driver_spin_profile === 'high' ? 'bg-orange-900/50 text-orange-300' : b.driver_spin_profile === 'low' ? 'bg-blue-900/50 text-blue-300' : 'bg-surface text-text-secondary'}`}>{b.driver_spin_profile || 'null'}</span></td>
                    <td className="p-2 text-center text-text-secondary text-xs">{b.feel || 'null'}</td>
                    <td className="p-2 text-center text-text-secondary">{b.compression || 'null'}</td>
                    <td className="p-2 text-right text-text-secondary">{b.msrp_cents ? `$${(b.msrp_cents / 100).toFixed(2)}` : '-'}</td>
                  </tr>
                  {selectedBall?.id === b.id && (
                    <tr className="bg-yellow-950/30 border-t border-yellow-800">
                      <td colSpan={6} className="p-4">
                        <div className="text-sm">
                          <div className="font-bold text-text-primary mb-2">{b.brand || 'null'} {b.model || 'null'}</div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div><span className="text-text-muted">Pieces:</span> <span className="text-text-primary">{b.piece_count || 'null'}</span></div>
                            <div><span className="text-text-muted">Cover:</span> <span className="text-text-primary">{b.cover_material || 'null'}</span></div>
                            <div><span className="text-text-muted">Compression:</span> <span className="text-text-primary">{b.compression || 'null'}</span></div>
                            <div><span className="text-text-muted">Feel:</span> <span className="text-text-primary">{b.feel || 'null'}</span></div>
                            <div><span className="text-text-muted">Driver Spin:</span> <span className="text-text-primary">{b.driver_spin_profile || 'null'}</span></div>
                            <div><span className="text-text-muted">Iron Spin:</span> <span className="text-text-primary">{b.iron_spin_profile || 'null'}</span></div>
                            <div><span className="text-text-muted">Wedge Spin:</span> <span className="text-text-primary">{b.wedge_spin_profile || 'null'}</span></div>
                            <div><span className="text-text-muted">Tour Ball:</span> <span className="text-text-primary">{b.is_tour_ball ? 'Yes' : 'No'}</span></div>
                          </div>
                          {b.notes && (
                            <div className="mt-3 pt-3 border-t border-yellow-800 text-xs text-text-secondary">
                              {b.notes}
                            </div>
                          )}
                          {b.msrp_cents && (
                            <div className="mt-3 pt-3 border-t border-yellow-800 flex gap-6 text-xs">
                              <div><span className="text-text-muted">Price:</span> <span className="text-emerald-400">${(b.msrp_cents / 100).toFixed(2)}/dozen</span></div>
                              <div><span className="text-text-muted">Spin Modifier:</span> <span className="text-text-primary">{b.spin_modifier}x</span></div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
